import React, { useCallback, useEffect, useState } from 'react';
import { ShieldQuestion, Loader2, Check, Undo2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Account-sharing review queue. Nothing here is banned - the tracker only
 *  ever flags an account for Ryan to look at. Also holds the one-click
 *  amnesty that undoes the old auto-ban behaviour. */
const SharingFlagsCard = ({ token, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/users/sharing/flags`, { headers });
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const clearFlag = async (id) => {
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/users/sharing/flags/${id}/clear`, { method: 'POST', headers });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const amnesty = async () => {
    if (!window.confirm(
      'Restore every account that was banned for account sharing, unblock their emails, '
      + 'clear all strikes and wipe the old device history? Use this once after the device-ID fix.'
    )) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`${API}/api/admin/users/sharing/amnesty`, { method: 'POST', headers });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Amnesty failed.');
      setMessage(
        `Restored ${d.accounts_restored} account(s), unblocked ${d.emails_unblocked} email(s), `
        + `cleared ${d.strikes_cleared} strike record(s), wiped ${d.device_rows_wiped} device row(s).`
      );
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const policy = data?.policy;
  const items = data?.items || [];

  return (
    <section
      className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4 mb-5"
      data-testid="sharing-flags-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.25em] text-red-400 flex items-center gap-2">
            <ShieldQuestion className="h-3.5 w-3.5" /> Account sharing review
          </p>
          <p className="text-xs text-zinc-500 leading-snug mt-1 max-w-2xl">
            {policy
              ? `Up to ${policy.allowed_devices} devices per account in ${policy.window_days} days. A device only counts once it has been used on ${policy.min_days_seen}+ separate days, and nothing is ever banned automatically - flagged accounts land here for you to decide.`
              : 'Loading policy…'}
          </p>
        </div>
        <button
          type="button"
          onClick={amnesty}
          disabled={busy}
          data-testid="sharing-amnesty-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 border border-zinc-700 text-white text-[10px] font-black uppercase tracking-widest"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
          Amnesty: undo sharing bans
        </button>
      </div>

      {message && (
        <p className="mt-3 rounded border border-emerald-500/40 bg-emerald-900/20 px-3 py-2 text-xs font-bold text-emerald-200" data-testid="sharing-amnesty-result">
          {message}
        </p>
      )}
      {error && (
        <p className="mt-3 flex items-start gap-2 rounded border border-red-500/50 bg-red-900/20 px-3 py-2 text-xs font-bold text-red-300">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
        </p>
      )}

      {items.length === 0 ? (
        <p className="text-xs text-zinc-600 mt-3" data-testid="sharing-flags-empty">
          No accounts flagged. {data?.legacy_sharing_bans ? `${data.legacy_sharing_bans} account(s) still carry an old sharing ban - run the amnesty above.` : ''}
        </p>
      ) : (
        <ul className="mt-3 space-y-2" data-testid="sharing-flags-list">
          {items.map((u) => (
            <li
              key={u.id}
              data-testid={`sharing-flag-row-${u.id}`}
              className="flex flex-wrap items-center gap-2 rounded border border-zinc-800 bg-black/40 px-3 py-2 text-xs"
            >
              <span className="text-white font-bold truncate max-w-[16rem]">{u.email}</span>
              <span className="text-zinc-500">{u.plan}</span>
              <span className="text-yellow-300">{u.sharing_flag_devices} devices</span>
              <span className="text-zinc-600">flagged {u.sharing_flag_count || 1}×</span>
              <button
                type="button"
                onClick={() => clearFlag(u.id)}
                disabled={busy}
                data-testid={`sharing-flag-clear-${u.id}`}
                className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 text-[10px] font-black uppercase tracking-widest"
              >
                <Check className="h-3 w-3" /> Looks fine
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default SharingFlagsCard;
